import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

/**
 * Catches any runtime error inside a subtree and shows a graceful fallback
 * instead of a blank screen. Lets the user click "Try again" (forces a
 * remount) or "Go home" to escape. Logs the full stack to the console so
 * we can still debug via browser DevTools.
 *
 * Usage: <ErrorBoundary pageName="Approvals"> <MyPage /> </ErrorBoundary>
 */
export default class ErrorBoundary extends React.Component {
  state = { err: null, info: null, resetKey: 0 };

  static getDerivedStateFromError(err) {
    return { err };
  }

  componentDidCatch(err, info) {
    this.setState({ info });
    console.error(`[ErrorBoundary:${this.props.pageName || 'page'}]`, err, info);
    // Fire a beacon so we can add server-side error tracking later if desired
    try {
      if (window.navigator?.sendBeacon) {
        window.navigator.sendBeacon(
          '/api/telemetry/client-error',
          JSON.stringify({
            page: this.props.pageName || 'unknown',
            message: err?.message || String(err),
            stack: (err?.stack || '').slice(0, 4000),
            at: new Date().toISOString(),
          })
        );
      }
    } catch (_) { /* non-fatal */ }
  }

  reset = () => this.setState(s => ({ err: null, info: null, resetKey: s.resetKey + 1 }));

  render() {
    if (!this.state.err) {
      // Use resetKey to force a full subtree remount on "Try again"
      return <React.Fragment key={this.state.resetKey}>{this.props.children}</React.Fragment>;
    }
    const pageName = this.props.pageName || 'This page';
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 p-6" data-testid="error-boundary-fallback">
        <div className="max-w-xl w-full bg-zinc-900 border border-red-500/40 rounded-lg p-6 shadow-2xl">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-red-500/15 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-red-400" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-white mb-1">Something went wrong on {pageName}</h2>
              <p className="text-xs text-zinc-400">The page hit an unexpected error. You can retry, or go back to the dashboard. Your work is safe.</p>
            </div>
          </div>
          <div className="bg-zinc-950 border border-zinc-800 rounded-md p-3 mb-4 max-h-40 overflow-auto">
            <p className="text-[11px] font-mono text-red-300 break-all">
              {this.state.err?.message || String(this.state.err)}
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={this.reset} data-testid="error-boundary-retry"
              className="flex-1 h-10 bg-amber-500 hover:bg-amber-400 text-zinc-950 text-xs font-bold rounded-md flex items-center justify-center gap-2 transition-colors">
              <RefreshCw className="w-3.5 h-3.5" /> Try again
            </button>
            <button onClick={() => { window.location.href = '/'; }} data-testid="error-boundary-home"
              className="h-10 px-4 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs rounded-md flex items-center gap-2 transition-colors">
              <Home className="w-3.5 h-3.5" /> Dashboard
            </button>
          </div>
          <p className="text-[10px] text-zinc-600 mt-3 text-center">
            Full stack has been logged to the browser console (press F12 → Console).
          </p>
        </div>
      </div>
    );
  }
}
